/**
 * Paste-detection heuristics for the AIInput pasteAsFile feature.
 * Pure module — no React, no DOM, no I/O.
 */

export type PasteKind = 'json' | 'xml' | 'csv' | 'log' | 'code' | 'text';

export interface DetectionOpts {
  /** Char count above which plain text always converts. Default: 500 */
  charThreshold?: number;
  /** Line count above which plain text always converts. Default: 10 */
  lineThreshold?: number;
  /** Run structured-content heuristics. Default: true */
  detectStructured?: boolean;
}

export interface PasteAnalysis {
  shouldConvert: boolean;
  kind: PasteKind;
}

const LARGE_TEXT_CHAR_THRESHOLD = 500;
const LARGE_TEXT_LINE_THRESHOLD = 10;
const STRUCTURED_DETECTION_MAX = 1_000_000;

const CODE_PATTERN =
  /^(import |from |export |function |class |const |let |var |def |package )/m;
const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}:\d{2}/m;
const STACK_TRACE_PATTERN = /^\s+at\s+/m;

export const KIND_TO_FILE: Readonly<
  Record<PasteKind, { ext: string; mime: string }>
> = Object.freeze({
  json: { ext: 'json', mime: 'application/json' },
  xml: { ext: 'xml', mime: 'application/xml' },
  csv: { ext: 'csv', mime: 'text/csv' },
  log: { ext: 'log', mime: 'text/plain' },
  code: { ext: 'txt', mime: 'text/plain' },
  text: { ext: 'txt', mime: 'text/plain' },
});

export function mapKindToFile(kind: PasteKind): { ext: string; mime: string } {
  return KIND_TO_FILE[kind];
}

/**
 * Single-pass paste analysis: splits lines once, runs each detector at most once.
 * Used by the hook to avoid the duplicate work of calling shouldConvertPaste
 * and detectPasteKind separately.
 */
export function analyzePaste(
  text: string,
  opts: DetectionOpts = {},
): PasteAnalysis {
  if (!text) return { shouldConvert: false, kind: 'text' };

  const charThreshold = opts.charThreshold ?? LARGE_TEXT_CHAR_THRESHOLD;
  const lineThreshold = opts.lineThreshold ?? LARGE_TEXT_LINE_THRESHOLD;
  const detectStructured = opts.detectStructured ?? true;

  const sizeTriggered = text.length > charThreshold;
  const lines = text.split('\n');
  const lineTriggered = lines.length > lineThreshold;
  const canDetect = detectStructured && text.length <= STRUCTURED_DETECTION_MAX;

  let kind: PasteKind = 'text';
  if (canDetect) {
    if (isJson(text)) kind = 'json';
    else if (isXmlOrHtml(text)) kind = 'xml';
    else if (isCode(text, lines)) kind = 'code';
    else if (isCsv(lines)) kind = 'csv';
    else if (isLogOutput(text, lines)) kind = 'log';
  }

  const shouldConvert = sizeTriggered || lineTriggered || kind !== 'text';
  return { shouldConvert, kind };
}

/**
 * Decide whether the pasted text should convert to a file attachment.
 * Thin wrapper over analyzePaste — kept for the public API and consumers
 * who only need the boolean.
 */
export function shouldConvertPaste(
  text: string,
  opts: DetectionOpts = {},
): boolean {
  return analyzePaste(text, opts).shouldConvert;
}

/**
 * Classify the paste kind. Returns 'text' when detection is disabled or the
 * text exceeds STRUCTURED_DETECTION_MAX (parse cost guard).
 */
export function detectPasteKind(
  text: string,
  opts: DetectionOpts = {},
): PasteKind {
  return analyzePaste(text, opts).kind;
}

function isJson(text: string): boolean {
  const trimmed = text.trimStart();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}

function isXmlOrHtml(text: string): boolean {
  const trimmed = text.trimStart();
  if (!trimmed.startsWith('<')) return false;
  return /<\/\w+>/.test(trimmed);
}

function isCode(text: string, lines: string[]): boolean {
  if (lines.length < 2) return false;
  return CODE_PATTERN.test(text);
}

function isCsv(lines: string[]): boolean {
  const nonEmpty = lines.filter((line) => line.trim().length > 0);
  if (nonEmpty.length < 3) return false;

  for (const delimiter of [',', '\t']) {
    const columnCounts = nonEmpty.map((line) => line.split(delimiter).length);
    const firstCount = columnCounts[0];
    if (firstCount < 2) continue;
    if (!columnCounts.every((c) => c === firstCount)) continue;
    // For comma delimiter, require min 2 commas per line. With consistent
    // column counts, comma count = firstCount - 1, so firstCount >= 3.
    if (delimiter === ',' && firstCount < 3) continue;
    return true;
  }
  return false;
}

function isLogOutput(text: string, lines: string[]): boolean {
  if (lines.length < 2) return false;
  const timestampedLines = lines.filter((l) => TIMESTAMP_PATTERN.test(l));
  if (timestampedLines.length >= 2) return true;
  return STACK_TRACE_PATTERN.test(text);
}
